import { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createAppointment, getEmployeePhotoUrl, previewAppointmentPricing } from '../../../../../services/PublicBookingService';
import AppointmentTicket from './AppointmentTicket';
import { formatFullAddress, formatNameList, formatNumberWithTwoDecimals, formatPhoneNumber } from '../../../../../utils/FormatUtils';

const StepConfirmation = ({
    companyUuid,
    companyInfo,
    branch,
    services,
    employee,
    date,
    time,
    customerData,
    totalDuration,
    totalPrice,
    appointmentResult,
    onConfirm,
    onBack
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmed, setConfirmed] = useState(!!appointmentResult);
    const [result, setResult] = useState(appointmentResult);
    const [pricing, setPricing] = useState(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    // O botão de confirmar fica no fim de uma tela longa e o erro nasce acima dele: sem
    // rolar até aqui, o cliente clica, nada muda na área visível e ele acha que o site
    // não respondeu.
    const errorRef = useRef(null);

    useEffect(() => {
        if (error && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [error]);

    const pricingItemsByServiceId = useMemo(() => {
        const map = new Map();
        (pricing?.items || []).forEach((item) => {
            map.set(item.serviceId, item);
        });
        return map;
    }, [pricing]);

    const resolvedTotalPrice = pricing?.finalTotal ?? totalPrice;
    // Um serviço de preço aberto já torna o total um ponto de partida. A marca vem do
    // cadastro (services) ou do cálculo do backend (pricing), o que estiver disponível.
    const hasPriceFrom = (services || []).some(service => service.priceFrom === true)
        || (pricing?.items || []).some(item => item.priceFrom === true);

    // Serviços do plano que ficaram sem o benefício porque a cota do ciclo acabou neste
    // pedido. Sem o aviso, o cliente via um serviço "incluso" passar a cobrar valor cheio
    // e achava que era erro do site.
    const limitReachedNames = (pricing?.items || [])
        .filter(item => item.subscriptionLimitReached)
        .map(item => item.serviceName);
    const benefitNames = (pricing?.items || [])
        .filter(item => item.subscriptionBenefitApplied)
        .map(item => item.serviceName);

    useEffect(() => {
        let active = true;

        const loadPricing = async () => {
            const selectedEmployeeId = employee?.assignedEmployee?.id || employee?.id;
            if (!companyUuid || !services?.length || !branch?.id || !selectedEmployeeId || selectedEmployeeId === 'random' || !customerData?.phone) {
                setPricing(null);
                return;
            }

            setPricingLoading(true);
            try {
                const formattedDate = date
                    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                    : null;
                const response = await previewAppointmentPricing(companyUuid, {
                    branchId: branch.id,
                    employeeId: selectedEmployeeId,
                    serviceIds: services.map(service => service.id),
                    date: formattedDate,
                    time,
                    customerName: getCustomerFullName(),
                    customerFirstName: customerData?.firstName,
                    customerLastName: customerData?.lastName,
                    customerPhone: customerData?.phone ? customerData.phone.replace(/\D/g, '') : null,
                    customerDocument: customerData?.document ? customerData.document.replace(/\D/g, '') : null
                });

                if (active) {
                    setPricing(response.data || null);
                }
            } catch (err) {
                if (active) {
                    setPricing(null);
                }
                console.error('Erro ao calcular preço do agendamento:', err);
            } finally {
                if (active) {
                    setPricingLoading(false);
                }
            }
        };

        loadPricing();

        return () => {
            active = false;
        };
    }, [companyUuid, branch?.id, employee, services, date, time, customerData?.firstName, customerData?.lastName, customerData?.phone, customerData?.document]);

    const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

    /** "Quinta-feira, 29 de Outubro de 2026": dia da semana e mês com inicial maiúscula, o resto como está. */
    const formatDateLong = (dateObj) => {
        if (!dateObj) return '';
        const weekday = capitalize(dateObj.toLocaleDateString('pt-BR', { weekday: 'long' }));
        const month = capitalize(dateObj.toLocaleDateString('pt-BR', { month: 'long' }));
        return `${weekday}, ${dateObj.getDate()} de ${month} de ${dateObj.getFullYear()}`;
    };

    /** Nome do dia da semana da data escolhida, com a inicial maiúscula. */
    const weekDayName = (dateObj) => {
        if (!dateObj) return '';
        const name = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });
        return name.charAt(0).toUpperCase() + name.slice(1);
    };

    const formatDateShort = (dateObj) => {
        if (!dateObj) return '';
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Obtém o nome do profissional (suporta diferentes formatos da API)
    const getEmployeeName = () => {
        if (result?.employee) {
            const resultEmployee = result.employee;
            return resultEmployee.name || resultEmployee.fullName || resultEmployee.firstName || 'Profissional';
        }
        if (employee?.isRandom) return 'Escolha automática';
        if (!employee) return '';
        if (employee.name) return employee.name;
        if (employee.fullName) return employee.fullName;
        if (employee.firstName && employee.lastName) {
            return `${employee.firstName} ${employee.lastName}`;
        }
        if (employee.firstName) return employee.firstName;
        return 'Profissional';
    };

    const getBranchAddress = () => {
        if (!branch?.address) return '';
        return typeof branch.address === 'object'
            ? formatFullAddress(branch.address)
            : branch.address;
    };

    const getCustomerFullName = () => {
        return [customerData?.firstName, customerData?.lastName].filter(Boolean).join(' ');
    };

    const getCustomerPhone = () => {
        return customerData?.phone ? formatPhoneNumber(customerData.phone) : '';
    };

    const serviceLabel = services.length === 1 ? t('booking.service') : t('booking.services');

    const handleConfirm = async () => {
        // A situação pode ter mudado depois da identificação; a confirmação só fica liberada
        // quando a cobrança segue regularizada. O servidor também valida isso ao criar.
        if (customerData?.clubPaymentOverdue) {
            setError('Você tem mensalidade do clube em aberto. Entre em contato para regularizar e agendar.');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const selectedEmployeeId = employee.assignedEmployee?.id || employee.id;
            if (!selectedEmployeeId || selectedEmployeeId === 'random') {
                setError('Não foi possível escolher um profissional disponível para este horário. Volte e selecione outro horário.');
                setLoading(false);
                return;
            }

            const appointmentData = {
                branchId: branch.id,
                employeeId: selectedEmployeeId,
                serviceIds: services.map(s => s.id),
                date: formatDateShort(date),
                time: time,
                customerName: getCustomerFullName(),
                customerFirstName: customerData.firstName,
                customerLastName: customerData.lastName,
                customerPhone: customerData.phone.replace(/\D/g, ''),
                customerEmail: customerData.email ? customerData.email.trim() : null,
                customerDocument: customerData.document ? customerData.document.replace(/\D/g, '') : null
            };

            const response = await createAppointment(companyUuid, appointmentData);
            setResult(response.data);
            setConfirmed(true);
            onConfirm(response.data);
        } catch (err) {
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError(t('booking.create_error'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Único botão de confirmar da revisão, no rodapé fixo da etapa. Havia outro no aviso do topo,
    // que só repetia este e disputava a atenção com o texto.
    const renderConfirmButton = () => (
        <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleConfirm}
            disabled={loading || customerData?.clubPaymentOverdue}
        >
            {loading ? (
                <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t('booking.confirming')}
                </>
            ) : (
                <>
                    <i className="fa fa-check me-2"></i>
                    {t('booking.confirm_button')}
                </>
            )}
        </Button>
    );

    // Tela de sucesso após confirmação
    const renderServicePrice = (service, itemFinalPrice, itemOriginalPrice, benefitApplied) => {
        if (pricingLoading) return <Spinner animation="border" size="sm" />;
        if (benefitApplied) {
            return (
                <>
                    <small className="text-muted text-decoration-line-through d-block">
                        R$ {formatNumberWithTwoDecimals(itemOriginalPrice)}
                    </small>
                    <strong className="text-success">
                        {Number(itemFinalPrice) === 0
                            ? 'Incluso no clube'
                            : `R$ ${formatNumberWithTwoDecimals(itemFinalPrice)}`}
                    </strong>
                </>
            );
        }
        // Só no preço cobrado: com benefício do clube quem manda é o desconto, e "incluso" não
        // admite ponto de partida.
        return <>{service.priceFrom ? 'A partir de ' : ''}R$ {formatNumberWithTwoDecimals(itemFinalPrice)}</>;
    };

    // Dados do comprovante, iguais na revisão e na tela de sucesso.
    const ticketProps = {
        time,
        dateLabel: formatDateLong(date),
        professional: {
            name: getEmployeeName(),
            isRandom: Boolean(employee?.isRandom),
            photoUrl: getEmployeePhotoUrl(companyUuid, employee?.id)
        },
        place: { name: branch?.name, address: getBranchAddress() },
        serviceLabel,
        services: services.map((service) => {
            const pricingItem = pricingItemsByServiceId.get(service.id);
            const finalPrice = pricingItem?.finalPrice ?? service.price ?? 0;
            const originalPrice = pricingItem?.originalPrice ?? service.price ?? 0;
            return {
                key: service.id,
                name: service.name,
                duration: service.duration || 30,
                priceNode: renderServicePrice(service, finalPrice, originalPrice, Boolean(pricingItem?.subscriptionBenefitApplied))
            };
        }),
        total: {
            durationLabel: `${totalDuration} min`,
            node: pricingLoading
                ? <Spinner animation="border" size="sm" />
                : <>{hasPriceFrom ? 'A partir de ' : ''}R$ {formatNumberWithTwoDecimals(resolvedTotalPrice)}</>
        }
    };

    if (confirmed && result) {
        return (
            <div className="step-success text-center py-4">
                <div className="success-icon mb-4">
                    <div className="success-circle">
                        <i className="fa fa-check fa-3x text-white"></i>
                    </div>
                </div>

                <h3 className="text-success mb-3">{t('booking.success_title')}</h3>
                <p className="text-muted mb-4">{t('booking.success_description')}</p>

                {/* Código do agendamento */}
                <Card className="appointment-code-card mb-4">
                    <Card.Body className="py-4">
                        <small className="text-white d-block mb-2">{t('booking.appointment_code')}</small>
                        <h2 className="appointment-uuid mb-0 font-monospace">
                            {result.uuid?.substring(0, 8).toUpperCase()}
                        </h2>
                    </Card.Body>
                </Card>

                {/* O mesmo comprovante da revisão: o cliente reconhece o que acabou de conferir. */}
                <div className="text-start">
                    <AppointmentTicket {...ticketProps} />
                </div>

                {/* Aviso importante */}
                <Alert variant="info" className="text-start">
                    <i className="fa fa-info-circle me-2"></i>
                    {t('booking.save_code_warning')}
                </Alert>

                {/* Sem botões de "novo agendamento" e "meus agendamentos" aqui: a barra de abas do rodapé
                    já oferece os dois caminhos (Agendar e Meus agendamentos). */}
            </div>
        );
    }

    // Tela de revisão antes de confirmar
    return (
        <div className="step-confirmation">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-check-circle me-2 text-primary"></i>
                    {t('booking.confirm_appointment')}
                </h4>
            </div>

            {/* Âmbar de propósito: é o aviso que impede o cliente de sair achando que já agendou.
                Fica logo abaixo do título, no lugar do subtítulo cinza que dizia o mesmo e passava batido. */}
            <Alert variant="warning" className="confirmation-notice mb-4">
                <i className="fa fa-triangle-exclamation confirmation-notice__icon" aria-hidden="true"></i>
                <div>
                    <strong className="d-block">Seu horário ainda não está reservado</strong>
                    <span>Toque em “{t('booking.confirm_button')}” para concluir. Depois disso, o agendamento estará confirmado.</span>
                </div>
            </Alert>

                {/* Com a cota acabando no meio do pedido o aviso abaixo já conta o que
                    ficou incluso; mostrar os dois deixaria "aplicado" sem a ressalva. */}
                {pricing?.subscriptionBenefitApplied && limitReachedNames.length === 0 && (
                    <Alert variant="success" className="mb-4">
                        <i className="fa fa-check-circle me-2"></i>
                        Benefício do clube aplicado neste agendamento.
                    </Alert>
                )}

                {limitReachedNames.length > 0 && (
                    <Alert variant="info" className="mb-4">
                        <i className="fa fa-circle-info me-2"></i>
                        <strong>
                            {benefitNames.length > 0
                                ? `${benefitNames.length === 1
                                    ? 'O benefício do clube que restava neste ciclo cobre'
                                    : 'Os benefícios do clube que restavam neste ciclo cobrem'} ${formatNameList(benefitNames)}.`
                                : 'Os benefícios do clube deste ciclo já foram usados.'}
                        </strong>{' '}
                        {formatNameList(limitReachedNames)} {limitReachedNames.length === 1 ? 'sai' : 'saem'} pelo valor normal.
                        Os serviços do plano voltam a ficar inclusos no próximo ciclo.
                    </Alert>
                )}

                {/* Não é erro: o agendamento acontece. O aviso existe porque o preço deste
                    dia não é o que o assinante está acostumado a pagar, e descobrir isso só
                    no caixa seria pior. */}
                {pricing?.subscriptionDayNotAllowed && (
                    <Alert variant="info" className="mb-4">
                        <i className="fa fa-circle-info me-2"></i>
                        <strong>{weekDayName(date)} não está entre os dias do seu plano.</strong>{' '}
                        {pricing.subscriptionOffDayDiscount > 0
                            ? `Você tem ${formatNumberWithTwoDecimals(pricing.subscriptionOffDayDiscount)}% de desconto neste dia, em vez do benefício integral.`
                            : 'Neste dia os serviços saem pelo valor cheio.'}{' '}
                        O benefício completo vale em {(pricing.subscriptionAllowedDays || []).join(', ')}.
                    </Alert>
                )}

            {error && (
                <Alert variant="danger" ref={errorRef} onClose={() => setError('')} dismissible>
                    <div className="d-flex align-items-start">
                        <i className="fa fa-exclamation-circle me-2 mt-1"></i>
                        <span>{error}</span>
                    </div>
                </Alert>
            )}

            {/* Comprovante: tudo o que o cliente precisa conferir num cartão só, do horário ao total.
                Antes a mesma informação aparecia em três blocos (cabeçalho, detalhes e resumo). */}
            <AppointmentTicket {...ticketProps} customer={{ name: getCustomerFullName(), phone: getCustomerPhone() }} />

            <div className="step-actions d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack} disabled={loading}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
                {renderConfirmButton()}
            </div>
        </div>
    );
};

export default StepConfirmation;
